/**
 * Guarded `dataLayer` pushes (GitLab #649, AC9).
 *
 * Anything reaching `window.dataLayer` is visible to every tag in the GTM
 * container, and from there to Google, Hotjar and anyone else the container
 * is configured to talk to. A customer's name, email, account number or
 * arrears balance must never make that trip — this is a debt-advice
 * platform, so a leaked field is special-category-adjacent data about a
 * financially vulnerable person, not a marketing inconvenience.
 *
 * The guard is deliberately blunt and drops rather than redacts. A dropped
 * parameter costs an analyst one dimension on a chart; a leaked one is a
 * reportable breach. When a key or value looks even slightly personal it
 * does not go.
 *
 * This is the only sanctioned way to send an event. Pushing to
 * `window.dataLayer` directly bypasses every check here.
 */

import { getDataLayer } from './consentMode';

/** Values a tag can meaningfully use. Objects and arrays are refused. */
export type DataLayerValue = string | number | boolean | null;

export type DataLayerParams = Record<string, unknown>;

/**
 * Key fragments that indicate a personal or financial field. Matched as
 * substrings against the lower-cased key, so `customer_first_name` and
 * `applicantEmail` are both caught.
 */
const BLOCKED_KEY_FRAGMENTS = [
  'name',
  'email',
  'phone',
  'mobile',
  'telephone',
  'address',
  'postcode',
  'postal',
  'dob',
  'birth',
  'age',
  'gender',
  'nino',
  'national_insurance',
  'account',
  'sortcode',
  'sort_code',
  'iban',
  'card',
  'balance',
  'amount',
  'income',
  'salary',
  'expenditure',
  'debt',
  'arrears',
  'creditor',
  'answer',
  'response',
  'comment',
  'note',
  'ip',
  'user_agent',
  'token',
  'password',
  'secret',
];

/**
 * Keys that contain one of the fragments above but are safe and useful.
 * Checked first, so the allowlist is explicit rather than an accident of
 * how the fragments happen to be spelled.
 */
const ALLOWED_KEYS = new Set([
  'event',
  'page_path',
  'page_title',
  'workflow_id',
  'workflow_step',
  'organisation_id',
  'organization_id',
  'consent_functional',
  'consent_analytics',
  'consent_marketing',
]);

/** Value shapes that are personal whatever the key is called. */
const BLOCKED_VALUE_PATTERNS: ReadonlyArray<RegExp> = [
  // someone@example.co.uk
  /[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/,
  // GB29 NWBK 6016 1331 9268 19
  /\b[A-Z]{2}[0-9]{2}(?:[ -]?[A-Z0-9]{4}){1,6}/,
  // UK mobile and landline numbers, spaced or not
  /(?:\+44|0)(?:[ -]?[0-9]){9,10}\b/,
  // Any run of six or more digits — account numbers, card fragments, NINOs
  /[0-9]{6,}/,
  // UK postcodes
  /\b[A-Z]{1,2}[0-9][A-Z0-9]?[ ]?[0-9][A-Z]{2}\b/i,
];

/** Whether a key is safe to send. */
export function isAllowedKey(key: string): boolean {
  if (ALLOWED_KEYS.has(key)) return true;
  const lower = key.toLowerCase();
  return !BLOCKED_KEY_FRAGMENTS.some((fragment) => lower.includes(fragment));
}

/**
 * Whether a value is safe to send. Only primitives are allowed: an object
 * would need recursive checking, and nothing we measure needs one.
 */
export function isAllowedValue(value: unknown): value is DataLayerValue {
  if (value === null) return true;
  if (typeof value === 'boolean' || typeof value === 'number') {
    return Number.isFinite(value as number) || typeof value === 'boolean';
  }
  if (typeof value !== 'string') return false;
  // Free text is not measurable and is where names hide, so cap it hard.
  if (value.length > 100) return false;
  return !BLOCKED_VALUE_PATTERNS.some((pattern) => pattern.test(value));
}

export interface SanitisedParams {
  params: Record<string, DataLayerValue>;
  /** Keys that were refused, for the development warning. */
  dropped: string[];
}

/** Strip everything the guard refuses, keeping the rest. */
export function sanitiseDataLayerParams(
  params: DataLayerParams,
): SanitisedParams {
  const clean: Record<string, DataLayerValue> = {};
  const dropped: string[] = [];

  for (const [key, value] of Object.entries(params)) {
    if (value === undefined) continue;
    if (!isAllowedKey(key) || !isAllowedValue(value)) {
      dropped.push(key);
      continue;
    }
    clean[key] = value;
  }

  return { params: clean, dropped };
}

/** Event names must be a stable, readable identifier a GTM trigger can match. */
const EVENT_NAME_PATTERN = /^[a-z][a-z0-9_]{2,49}$/;

/**
 * Push an event to the data layer.
 *
 * Returns `false` when nothing was sent, so a caller can assert on it in a
 * test. Silently doing nothing is the correct runtime behaviour — a
 * measurement call must never break a customer's journey — but a silent
 * failure nobody can detect is how a broken funnel survives to production.
 *
 * No consent check here: GTM is not on the page at all until an optional
 * category is granted, so a push before consent lands in an array no tag is
 * reading. Duplicating the check would imply this is the thing keeping the
 * data private, which it is not.
 */
export function pushDataLayerEvent(
  event: string,
  params: DataLayerParams = {},
): boolean {
  if (!EVENT_NAME_PATTERN.test(event)) {
    warnInDevelopment(`Refused data layer event with invalid name "${event}".`);
    return false;
  }

  const dataLayer = getDataLayer();
  if (!dataLayer) return false;

  const { params: clean, dropped } = sanitiseDataLayerParams(params);
  if (dropped.length > 0) {
    warnInDevelopment(
      `Dropped ${dropped.length} parameter(s) from "${event}": ${dropped.join(', ')}. ` +
        'Analytics events must carry no personal or financial data.',
    );
  }

  dataLayer.push({ event, ...clean });
  return true;
}

/**
 * Push the `gtm.js` start event.
 *
 * Part of Google's own container snippet, and easy to lose when the snippet
 * is split up: GTM's "Initialisation" and "All Pages" triggers key off this
 * event, so a container loaded without it sits there measuring nothing.
 *
 * Bypasses the guard because `gtm.start` is a timestamp and the event name
 * contains a dot, neither of which the guard permits — and neither of which
 * carries anything personal.
 */
export function pushGtmStart(now: number = Date.now()): boolean {
  const dataLayer = getDataLayer();
  if (!dataLayer) return false;
  if (dataLayer.some((entry) => (entry as Record<string, unknown>)?.event === 'gtm.js')) {
    // Re-pushing on a client-side navigation would re-fire every All Pages
    // tag, double-counting the pageview.
    return false;
  }
  dataLayer.push({ 'gtm.start': now, event: 'gtm.js' });
  return true;
}

/**
 * Warns during development only. The message names the offending keys but
 * never their values — a console warning is itself a place personal data
 * should not appear.
 */
function warnInDevelopment(message: string): void {
  if (process.env.NODE_ENV === 'production') return;
  console.warn(`[analytics] ${message}`);
}
